/**
 * Read-only share links, client side.
 *
 * Shared mode: the page was opened at /s/<token>. Every API call goes to /api/shared/<token>/...,
 * which the Worker scopes to the link's single project; the UI hides the switcher, Conversions,
 * Recent visits and the Share control (body.shared).
 *
 * Admin mode: the Share control opens a panel to create, copy and revoke links for the current project.
 */

const m = location.pathname.match(/^\/s\/([A-Za-z0-9_-]{32,64})\/?$/);
export const shareToken: string | null = m ? m[1]! : null;
export const shared = shareToken !== null;

/** Where an API path is served from: the admin API, or the token's read-only API. */
export const apiUrl = (path: string) => (shareToken ? `/api/shared/${shareToken}/${path}` : `/api/${path}`);

/** Static files: a shared page loads them through /s/assets/ so one Access bypass covers it. */
export const assetUrl = (p: string) => (shareToken ? `/s/assets${p}` : p);

export function applySharedMode(label?: string): void {
  if (!shared) return;
  document.body.classList.add("shared");
  if (label) document.title = `${label} · MonoLog`;
}

// ---------- admin panel ----------

interface ShareLink {
  id: string;
  label: string;
  created_at: number;
  expires_at: number | null;
  revoked_at: number | null;
  url?: string;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const day = (t: number) => new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

let getProject: () => { id: string; name: string } = () => ({ id: "", name: "" });
let fresh: ShareLink | null = null;
let err = "";

async function call<T>(method: "GET" | "POST" | "DELETE", path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/${path}`, {
    method,
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  if (!res.ok) throw new Error((out as { error?: string }).error ?? `HTTP ${res.status}`);
  return out as T;
}

function panel(): HTMLElement {
  let el = document.getElementById("share-panel");
  if (!el) {
    el = document.createElement("div");
    el.id = "share-panel";
    el.className = "share-back";
    el.hidden = true;
    document.body.append(el);
  }
  return el;
}

function status(l: ShareLink): string {
  if (l.revoked_at !== null) return `<span class="pill poor">Revoked</span>`;
  if (l.expires_at !== null && l.expires_at <= Date.now()) return `<span class="pill none">Expired</span>`;
  return `<span class="pill good">Active</span>`;
}

async function render(): Promise<void> {
  const el = panel();
  const p = getProject();
  let list: ShareLink[] = [];
  let loadErr = "";
  try {
    list = await call<ShareLink[]>("GET", `shares?project=${encodeURIComponent(p.id)}`);
  } catch (e) {
    loadErr = (e as Error).message;
  }
  const rows = list.length
    ? list
        .map(
          (l) => `<li><div class="share-l"><b>${esc(l.label)}</b> ${status(l)}<span>Created ${day(l.created_at)}${l.expires_at ? ` · ${l.expires_at > Date.now() ? "expires" : "expired"} ${day(l.expires_at)}` : " · never expires"}</span></div>
        ${l.revoked_at === null ? `<button class="btn" data-share-revoke="${esc(l.id)}">Revoke</button>` : ""}</li>`,
        )
        .join("")
    : `<li class="share-empty">No links yet.</li>`;
  el.innerHTML = `<section class="card share-card" role="dialog" aria-modal="true" aria-labelledby="share-h">
    <div class="card-head"><h2 id="share-h">Share ${esc(p.name)}</h2><span class="spacer"></span><button class="link" data-share-close aria-label="Close">Close</button></div>
    <p class="share-note">Anyone with the link can view this project's numbers. They can't edit anything, switch projects or see individual visits.</p>
    <form class="share-form" data-share-form>
      <label class="fld"><span>Label</span><input class="in" name="label" maxlength="100" required placeholder="e.g. Client weekly report"></label>
      <label class="fld narrow"><span>Expires</span><select class="in" name="expires"><option value="">Never</option><option value="7">In 7 days</option><option value="30">In 30 days</option><option value="90">In 90 days</option></select></label>
      <button class="btn primary" type="submit">Create link</button>
    </form>
    ${err ? `<div class="form-err">${esc(err)}</div>` : ""}
    ${fresh?.url ? `<div class="share-new"><span>Copy this link now. It won't be shown again.</span><div class="share-copy"><input class="in" readonly value="${esc(fresh.url)}" aria-label="Share link"><button class="btn primary" data-share-copy>Copy</button></div></div>` : ""}
    ${loadErr ? `<div class="form-err">${esc(loadErr)}</div>` : `<ul class="share-list">${rows}</ul>`}
  </section>`;
}

function open(): void {
  fresh = null;
  err = "";
  panel().hidden = false;
  void render();
}

function close(): void {
  panel().hidden = true;
  fresh = null;
}

export function initShare(project: () => { id: string; name: string }): void {
  if (shared) return;
  getProject = project;
  document.addEventListener("click", (ev) => {
    const t = ev.target as HTMLElement;
    if (t.closest("[data-share-open]")) return open();
    if (t.closest("[data-share-close]") || t.id === "share-panel") return close();
    if (t.closest("[data-share-copy]")) {
      const input = panel().querySelector<HTMLInputElement>(".share-copy input");
      if (!input) return;
      input.select();
      void navigator.clipboard?.writeText(input.value).then(() => {
        const b = t.closest<HTMLButtonElement>("[data-share-copy]");
        if (b) b.textContent = "Copied";
      });
      return;
    }
    const rev = t.closest<HTMLElement>("[data-share-revoke]");
    if (rev)
      void call("DELETE", `shares/${rev.dataset.shareRevoke}`)
        .then(() => ((err = ""), render()))
        .catch((e) => ((err = (e as Error).message), render()));
  });
  document.addEventListener("submit", (ev) => {
    const form = (ev.target as HTMLElement).closest<HTMLFormElement>("[data-share-form]");
    if (!form) return;
    ev.preventDefault();
    const f = new FormData(form);
    const expires = String(f.get("expires") ?? "");
    void call<ShareLink>("POST", "shares", {
      project: getProject().id,
      label: String(f.get("label") ?? ""),
      expires_days: expires ? Number(expires) : null,
    })
      .then((l) => ((fresh = l), (err = ""), render()))
      .catch((e) => ((err = (e as Error).message), render()));
  });
  document.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape" && !panel().hidden) close();
  });
}
